import { useRender } from '@base-ui/react/use-render';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import {
    createContext,
    useContext,
    useId,
    useRef,
    type ComponentPropsWithRef,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    type RefObject,
} from 'react';
import {
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverIconClose,
    PopoverTitle,
    type PopoverContentProps,
} from '../containers/popover.tsx';
import {
    Menu,
    MenuGroup,
    MenuItem,
    MenuItemLabel,
    MenuItemLeading,
    MenuItemTrailing,
    type MenuGroupProps,
    type MenuItemLabelProps,
    type MenuItemLeadingProps,
    type MenuItemProps,
    type MenuItemTrailingProps,
    type MenuProps,
} from '../primitives/menu.tsx';
import { ToolbarButton } from '../primitives/toolbar-button.tsx';
import { cn } from '../util/classes.ts';
import {
    flattenOptions,
    optionLabel,
    optionValue,
    type Option,
    type OptionGroup,
    type OptionKey,
} from '../util/options.ts';
import { useControllableState } from '../util/use-controllable-state.tsx';
import { EmptyState, type EmptyStateProps } from './empty-state.tsx';

/** The default option shape: `label` is what the row and the field show, `value` identifies. */
export type SelectOption = Option;

/** A labelled section of options; `value` is both its key and its heading. */
export type SelectOptionGroup<Item> = OptionGroup<Item>;

/** A context cannot be generic, so the options travel as `unknown`; `useSelect` types them. */
type SelectContextValue = {
    title: string;
    /** Ties the trigger to the listbox it opens. */
    listId: string;
    items: readonly unknown[] | undefined;
    value: string | undefined;
    /** The option `value` picks out of `items`, for the parts that render it. */
    selectedItem: unknown;
    setValue: (item: unknown) => void;
    isSelected: (item: unknown) => boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
    anchorRef: RefObject<HTMLElement | null>;
};

const SelectContext = createContext<SelectContextValue | undefined>(undefined);

function useSelectContext(): SelectContextValue {
    const context = useContext(SelectContext);

    if (context === undefined) {
        throw new Error('Select parts have to be rendered inside a <Select>.');
    }

    return context;
}

export type UseSelectResult<Item> = {
    title: string;
    listId: string;
    /** What a template iterates: the options, or the sections holding them. */
    items: readonly Item[] | undefined;
    /** What is picked, as the caller holds it: the option's `value`. */
    value: string | undefined;
    /** The option `value` names, once `items` can be searched for it. */
    selectedItem: Item | undefined;
    setValue: (item: Item | undefined) => void;
    isSelected: (item: Item) => boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
    anchorRef: RefObject<HTMLElement | null>;
};

/** The control's state, for a field or a surface of your own; `Item` is yours to name. */
export function useSelect<Item = SelectOption>(): UseSelectResult<Item> {
    return useSelectContext() as UseSelectResult<Item>;
}

type SelectItemContextValue = {
    isSelected: boolean;
};

const SelectItemContext = createContext<SelectItemContextValue>({ isSelected: false });

/** `Item` comes from `items`, which is also what a `value` is resolved against. */
export type SelectProps<Item = SelectOption> = {
    children?: ReactNode;
    /** Names the control: the surface's accessible name, and its title on mobile. */
    title: string;
    /**
     * The options, flat or as `{ value, items }` sections. `SelectList` iterates them, and
     * the field looks `value` up in them to show a label instead of the bare value.
     */
    items?: readonly Item[] | readonly SelectOptionGroup<Item>[];
    /** What is picked, as its option's `value`. */
    value?: OptionKey<Item>;
    defaultValue?: OptionKey<Item>;
    onValueChange?: (value: OptionKey<Item> | undefined) => void;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
};

/**
 * Select — pick one option from a short list, without search.
 *
 * The value is the option's `value`, so callers hold the same key they store, and `items`
 * is how the field turns it back into a label. A row still carries the whole option, so it
 * can render an icon or a subtitle the value knows nothing about.
 *
 * @example
 * <Select title="Kategorie" items={categories} value={category} onValueChange={setCategory}>
 *     <SelectTrigger>
 *         <SelectValue placeholder="Kategorie wählen" />
 *     </SelectTrigger>
 *     <SelectContent>
 *         <SelectList>
 *             {(option: Category) => <SelectItem key={option.value} value={option} />}
 *         </SelectList>
 *     </SelectContent>
 * </Select>
 */
export function Select<Item = SelectOption>(props: SelectProps<Item>): ReactElement {
    const {
        children,
        title,
        items,
        onValueChange,
        open: openProp,
        defaultOpen = false,
        onOpenChange,
    } = props;
    const listId = useId();
    const anchorRef = useRef<HTMLElement>(null);

    const [open, setOpen] = useControllableState({
        value: openProp,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });

    // The empty state *is* `undefined`, so a value has to travel as a list for the hook to
    // see that the control is nonetheless controlled.
    const [values, commit] = useControllableState<readonly string[]>({
        value: 'value' in props ? (props.value === undefined ? [] : [props.value]) : undefined,
        defaultValue: props.defaultValue === undefined ? [] : [props.defaultValue],
        onChange: (next) => {
            // Every key in `values` came out of `optionValue` on one of the options.
            onValueChange?.(next[0] as OptionKey<Item> | undefined);
        },
    });
    const value = values[0];

    return (
        <SelectContext.Provider
            value={{
                title,
                listId,
                items,
                value,
                selectedItem:
                    value === undefined
                        ? undefined
                        : flattenOptions(items).find((item) => optionValue(item) === value),
                setValue: (next) => {
                    const selectedValue = next === undefined ? undefined : optionValue(next);

                    if (selectedValue === value) {
                        return;
                    }

                    commit(selectedValue === undefined ? [] : [selectedValue]);
                },
                isSelected: (item) => values.includes(optionValue(item)),
                open,
                setOpen,
                anchorRef,
            }}
        >
            {children}
        </SelectContext.Provider>
    );
}

export type SelectTriggerProps = useRender.ComponentProps<'button'>;

/**
 * The field — it shows what is selected and opens the surface, and writes its own chevron.
 *
 * `render` makes it something else: a chip, a badge, a row. That keeps the ARIA, the
 * anchoring and the click, and drops the field's look and chevron — pass a button, since
 * nothing else can be activated from the keyboard.
 */
export function SelectTrigger({ className, children, render, ...props }: SelectTriggerProps): ReactElement {
    const { open, setOpen, anchorRef, listId } = useSelectContext();
    const isField = render === undefined;

    return useRender({
        // The field is the default element, so a `render` element brings its own look.
        render: render ?? <ToolbarButton />,
        ref: anchorRef,
        props: {
            role: 'combobox',
            'aria-haspopup': 'listbox',
            'aria-expanded': open,
            'aria-controls': listId,
            'data-slot': 'select-trigger',
            'data-state': open ? 'open' : 'closed',
            ...props,
            onClick: (event: MouseEvent<HTMLButtonElement>) => {
                props.onClick?.(event);

                if (!event.defaultPrevented) {
                    setOpen(true);
                }
            },
            className: isField ? cn('justify-start text-left', className) : className,
            children: isField ? (
                <>
                    {children}
                    <SelectTriggerChevron />
                </>
            ) : (
                children
            ),
        },
    });
}

export type SelectTriggerChevronProps = ComponentPropsWithRef<'svg'>;

/** The chevron that turns as the surface opens; the default field renders it already. */
export function SelectTriggerChevron({ className, ...props }: SelectTriggerChevronProps): ReactElement {
    const { open } = useSelectContext();

    return (
        <ChevronDownIcon
            data-slot="select-trigger-chevron"
            className={cn(
                'ml-auto size-4 shrink-0 text-content-weaker',
                'transition-transform duration-state ease-state',
                open && 'rotate-180',
                className,
            )}
            {...props}
        />
    );
}

export type SelectValueProps<Item = SelectOption> = Omit<ComponentPropsWithRef<'span'>, 'children'> & {
    /** Shown while nothing is picked, dimmed like the placeholder it is. */
    placeholder?: string;
    /** A template called with the picked option. Renders its label by default. */
    children?: (item: Item) => ReactNode;
};

/**
 * What the trigger shows: the picked option, or the `placeholder` while there is none. Text
 * in a direct child `span` truncates, so a long label cannot push the chevron out.
 *
 * The template is called with the option `value` names — which needs `items` to hold it. A
 * value no option accounts for falls back to the value itself, the way a `<select>` would.
 */
export function SelectValue<Item = SelectOption>({
    placeholder,
    className,
    children = (item) => <span>{optionLabel(item)}</span>,
    ...props
}: SelectValueProps<Item>): ReactElement {
    const { value, selectedItem } = useSelect<Item>();

    return (
        <span
            data-slot="select-value"
            data-placeholder={value === undefined || undefined}
            className={cn(
                'inline-flex min-w-0 flex-1 items-center gap-cozy',
                "*:[svg:not([class*='size-'])]:size-4",
                '*:[span]:min-w-0 *:[span]:truncate',
                value === undefined && 'text-content-weaker',
                className,
            )}
            {...props}
        >
            {selectValueContent(value, selectedItem, placeholder, children)}
        </span>
    );
}

/** The picked option, the value itself when no option accounts for it, or the placeholder. */
function selectValueContent<Item>(
    value: string | undefined,
    selectedItem: Item | undefined,
    placeholder: string | undefined,
    children: (item: Item) => ReactNode,
): ReactNode {
    if (value === undefined) {
        return placeholder === undefined ? null : <span>{placeholder}</span>;
    }

    if (selectedItem === undefined) {
        return <span>{value}</span>;
    }

    return children(selectedItem);
}

export type SelectContentProps = Omit<PopoverContentProps, 'anchor' | 'children'> & {
    children?: ReactNode;
};

/** The surface the items live on — anchored to the field on desktop, a sheet on mobile. */
export function SelectContent({ children, className, ...props }: SelectContentProps): ReactElement {
    const { title, open, setOpen, anchorRef } = useSelectContext();

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverContent
                anchor={anchorRef}
                aria-label={title}
                data-slot="select-content"
                className={cn(
                    'sm:max-h-[min(24rem,var(--available-height))] sm:min-w-(--anchor-width) sm:max-w-105',
                    className,
                )}
                {...props}
            >
                <PopoverHeader>
                    <PopoverTitle className="text-card-title">{title}</PopoverTitle>
                    <PopoverIconClose data-slot="select-close" />
                </PopoverHeader>

                <PopoverBody className="px-0 py-cozy sm:px-0">{children}</PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

export type SelectEmptyProps = EmptyStateProps;

/** What the surface shows instead of a list when there is nothing to pick. */
export function SelectEmpty({ children, ...props }: SelectEmptyProps): ReactElement {
    return (
        <EmptyState data-slot="select-empty" {...props}>
            {children}
        </EmptyState>
    );
}

export type SelectListProps<Item = SelectOption> = Omit<MenuProps, 'children'> & {
    /** A template called per entry of `items`, or the rows written out by hand. */
    children?: ReactNode | ((item: Item, index: number) => ReactNode);
};

/**
 * The listbox holding the `SelectItem`s. A template is called with each entry of `items` —
 * a section where those are grouped, which `SelectGroup` renders.
 */
export function SelectList<Item = SelectOption>({
    className,
    children,
    ...props
}: SelectListProps<Item>): ReactElement {
    const { title, listId, items } = useSelect<Item>();

    return (
        <Menu
            id={listId}
            role="listbox"
            aria-label={title}
            data-slot="select-list"
            className={cn('gap-tight py-0', className)}
            {...props}
        >
            {selectRows(children, items)}
        </Menu>
    );
}

/** Rows from a template over the entries it belongs to, or the ones written by hand. */
function selectRows<Item>(
    children: ReactNode | ((item: Item, index: number) => ReactNode),
    items: readonly Item[] | undefined,
): ReactNode {
    if (typeof children !== 'function') {
        return children;
    }

    return (items ?? []).map((item, index) => children(item, index));
}

export type SelectGroupProps<Item = SelectOption> = Omit<MenuGroupProps, 'children'> & {
    /** The section's options, which its template is called with. */
    items?: readonly Item[];
    children?: ReactNode | ((item: Item, index: number) => ReactNode);
};

/** A labelled section of the list. Items inside it are picked like any other. */
export function SelectGroup<Item = SelectOption>({
    className,
    items,
    children,
    ...props
}: SelectGroupProps<Item>): ReactElement {
    return (
        <MenuGroup data-slot="select-group" className={className} {...props}>
            {selectRows(children, items)}
        </MenuGroup>
    );
}

export type SelectItemProps<Item = SelectOption> = Omit<MenuItemProps, 'label' | 'value'> & {
    value: Item;
    /** The row's text, and its accessible name. Defaults to the option's own label. */
    label?: string;
};

/** One option. Without children it renders its label, and a check when selected. */
export function SelectItem<Item = SelectOption>({
    value,
    label,
    children,
    onClick,
    ...props
}: SelectItemProps<Item>): ReactElement {
    const { isSelected, setValue, setOpen } = useSelectContext();
    const selected = isSelected(value);

    return (
        <SelectItemContext.Provider value={{ isSelected: selected }}>
            <MenuItem
                role="option"
                aria-selected={selected}
                data-slot="select-item"
                label={label ?? optionLabel(value)}
                onClick={(event) => {
                    onClick?.(event);

                    if (!event.defaultPrevented) {
                        setValue(value);
                        setOpen(false);
                    }
                }}
                {...props}
            >
                {children ?? (
                    <>
                        <SelectItemLabel />
                        <SelectItemIndicator />
                    </>
                )}
            </MenuItem>
        </SelectItemContext.Provider>
    );
}

export type SelectItemLeadingProps = MenuItemLeadingProps;

/** The item's icon. */
export function SelectItemLeading({ className, children, ...props }: SelectItemLeadingProps): ReactElement {
    return (
        <MenuItemLeading
            data-slot="select-item-leading"
            className={cn("text-content-weak [&_svg:not([class*='size-'])]:size-4", className)}
            {...props}
        >
            {children}
        </MenuItemLeading>
    );
}

export type SelectItemLabelProps = MenuItemLabelProps;

/** The item's text. Without children it renders the item's own label. */
export function SelectItemLabel({ className, children, ...props }: SelectItemLabelProps): ReactElement {
    return (
        <MenuItemLabel data-slot="select-item-label" className={className} {...props}>
            {children}
        </MenuItemLabel>
    );
}

export type SelectItemIndicatorProps = MenuItemTrailingProps;

/** The check that marks the selected item; nothing on the others. */
export function SelectItemIndicator({ children, ...props }: SelectItemIndicatorProps): ReactElement | null {
    const { isSelected } = useContext(SelectItemContext);

    if (!isSelected) {
        return null;
    }

    return (
        <MenuItemTrailing data-slot="select-item-indicator" {...props}>
            {children ?? <CheckIcon className="size-4 text-brand" strokeWidth={2.5} />}
        </MenuItemTrailing>
    );
}
